import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { PayrollProvisionService } from '../../services/payrollProvision.service';
import { IGratuityLine, IGratuityPreview, IGratuityRun, IGratuityRunDetail } from '../../interfaces/payrollProvision.interface';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import { humanizeEnum } from '../../utils/format';
import {
    ConfirmDialog, JournalLink, PeriodPicker, PreviewNotices, RunStatusChip, SummaryStats, currentPeriod, money,
    periodLabel, useSegmentNames, years,
} from './provisionShared';

interface Props {
    institutionId: string;
    canManage: boolean;
}

// W3-J: monthly gratuity provision. The preview is the draft; posting creates the run and its journal.
const GratuityProvisionTab: React.FC<Props> = ({ institutionId, canManage }) => {
    const { showSuccess, showApiError } = useToast();
    const segmentName = useSegmentNames(institutionId);
    const [period, setPeriod] = useState(currentPeriod);
    const [preview, setPreview] = useState<IGratuityPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [posting, setPosting] = useState(false);
    const [runs, setRuns] = useState<IGratuityRun[]>([]);
    const [loadingRuns, setLoadingRuns] = useState(false);
    const [detail, setDetail] = useState<IGratuityRunDetail | null>(null);
    const [reverseTarget, setReverseTarget] = useState<IGratuityRun | null>(null);
    const [reversing, setReversing] = useState(false);

    const loadRuns = useCallback(async () => {
        if (!institutionId) return;
        setLoadingRuns(true);
        try {
            setRuns(await PayrollProvisionService.gratuityRuns(institutionId));
        } catch (e) {
            showApiError(e, 'Failed to load gratuity runs');
        } finally {
            setLoadingRuns(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { loadRuns(); }, [loadRuns]);

    const runPreview = async () => {
        setPreviewing(true);
        try {
            setPreview(await PayrollProvisionService.gratuityPreview(institutionId, period.year, period.month));
        } catch (e) {
            setPreview(null);
            showApiError(e, 'Failed to preview the gratuity provision');
        } finally {
            setPreviewing(false);
        }
    };

    const post = async () => {
        setPosting(true);
        try {
            await PayrollProvisionService.gratuityPost(institutionId, period);
            showSuccess(`Gratuity provision for ${periodLabel(period.year, period.month)} posted`);
            setPreview(null);
            await loadRuns();
        } catch (e) {
            showApiError(e, 'Failed to post the gratuity provision');
        } finally {
            setPosting(false);
        }
    };

    const reverse = async () => {
        if (!reverseTarget) return;
        setReversing(true);
        try {
            await PayrollProvisionService.gratuityReverse(reverseTarget.id);
            showSuccess('Gratuity run reversed');
            setReverseTarget(null);
            await loadRuns();
        } catch (e) {
            showApiError(e, 'Failed to reverse the gratuity run');
        } finally {
            setReversing(false);
        }
    };

    const openDetail = async (id: string) => {
        try {
            setDetail(await PayrollProvisionService.gratuityRun(id));
        } catch (e) {
            showApiError(e, 'Failed to load the run');
        }
    };

    const lineColumns = (withSettlement: boolean): Column<IGratuityLine>[] => [
        { key: 'emp', header: 'Employee', priority: 1, helpKey: 'payroll.gratuity.line.col.emp', render: l => `${l.employeeName}${l.employeeCode ? ` (${l.employeeCode})` : ''}` },
        { key: 'seg', header: 'Segment', priority: 3, helpKey: 'payroll.gratuity.line.col.seg', render: l => segmentName(l.segmentId) },
        { key: 'yrs', header: 'Service years', priority: 2, align: 'right', helpKey: 'payroll.gratuity.line.col.yrs', render: l => years(l.serviceYears) },
        { key: 'rate', header: 'Day rate', priority: 3, align: 'right', helpKey: 'payroll.gratuity.line.col.rate', render: l => money(l.dayRate) },
        { key: 'dpy', header: 'Days per year', priority: 3, align: 'right', helpKey: 'payroll.gratuity.line.col.dpy', render: l => l.daysPerYear },
        { key: 'liab', header: 'Liability', priority: 1, align: 'right', helpKey: 'payroll.gratuity.line.col.liab', render: l => money(l.liability) },
        { key: 'carr', header: 'Carried', priority: 2, align: 'right', helpKey: 'payroll.gratuity.line.col.carr', render: l => money(l.carried) },
        { key: 'mov', header: 'Movement', priority: 1, align: 'right', helpKey: 'payroll.gratuity.line.col.mov', render: l => money(l.movement) },
        ...(withSettlement
            ? [{ key: 'set', header: 'Settlement', priority: 2, align: 'right' as const, helpKey: 'payroll.gratuity.line.col.set', render: (l: IGratuityLine) => money(l.settlementAmount) }]
            : []),
        { key: 'note', header: 'Note', priority: 4, helpKey: 'payroll.gratuity.line.col.note', render: l => l.note || '—' },
    ];

    const runColumns: Column<IGratuityRun>[] = [
        { key: 'period', header: 'Period', priority: 1, helpKey: 'payroll.gratuity.run.col.period', render: r => periodLabel(r.year, r.month) },
        { key: 'kind', header: 'Kind', priority: 2, helpKey: 'payroll.gratuity.run.col.kind', render: r => (r.kind === 'UTILISATION' ? 'Utilisation (settlement)' : 'Provision') },
        { key: 'eff', header: 'Effective', priority: 3, helpKey: 'payroll.gratuity.run.col.eff', render: r => formatDate(r.effectiveDate) },
        { key: 'status', header: 'Status', priority: 1, helpKey: 'payroll.gratuity.run.col.status', render: r => <RunStatusChip status={r.status} /> },
        { key: 'emp', header: 'Employees', priority: 3, align: 'right', helpKey: 'payroll.gratuity.run.col.emp', render: r => r.employeeCount },
        { key: 'liab', header: 'Liability', priority: 2, align: 'right', helpKey: 'payroll.gratuity.run.col.liab', render: r => money(r.totalLiability) },
        { key: 'carr', header: 'Carried', priority: 3, align: 'right', helpKey: 'payroll.gratuity.run.col.carr', render: r => money(r.totalCarried) },
        { key: 'mov', header: 'Movement', priority: 1, align: 'right', helpKey: 'payroll.gratuity.run.col.mov', render: r => money(r.totalMovement) },
        { key: 'je', header: 'Journal', priority: 3, helpKey: 'payroll.gratuity.run.col.je', render: r => <JournalLink id={r.journalEntryId} /> },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.gratuity.run.col.actions', role: 'actions', hideInCard: true,
            render: r => (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" data-help="payroll.gratuity.run.action.details" onClick={() => openDetail(r.id)}>Details</Button>
                    {canManage && r.kind === 'PROVISION' && r.status === 'POSTED' && (
                        <Button size="small" color="error" data-help="payroll.gratuity.run.action.reverse" onClick={() => setReverseTarget(r)}>Reverse</Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box data-help-page="payroll.gratuity.page">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Previewing shows the draft only; nothing is stored until you post the run. Posting books the movement
                between each employee's accrued gratuity liability and what is already carried in the provision account.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="payroll.gratuity.toolbar">
                <PeriodPicker year={period.year} month={period.month} onChange={p => { setPeriod(p); setPreview(null); }} />
                <Button data-help="payroll.gratuity.preview" variant="outlined" onClick={runPreview} disabled={!institutionId || previewing}>Preview</Button>
                {canManage && (
                    <Button data-help="payroll.gratuity.post" variant="contained" onClick={post} disabled={!preview || !!preview.blocker || posting}>Post run</Button>
                )}
            </Box>

            {previewing && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
            {preview && !previewing && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Draft for {periodLabel(preview.year, preview.month)} (as of {formatDate(preview.asOf)})
                    </Typography>
                    <PreviewNotices blocker={preview.blocker} warnings={preview.warnings ?? []} />
                    <SummaryStats items={[
                        { label: 'Employees', value: String(preview.employeeCount) },
                        { label: 'Total liability', value: money(preview.totalLiability) },
                        { label: 'Total carried', value: money(preview.totalCarried) },
                        { label: 'Total movement', value: money(preview.totalMovement) },
                    ]} />
                    <ResponsiveTable data-help-section="payroll.gratuity.previewTable" pageKey="gratuity-preview" columns={lineColumns(false)} rows={preview.lines}
                        getRowKey={l => l.userId} emptyMessage="No employees are eligible for gratuity in this period." />
                </Box>
            )}

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Runs</Typography>
            {loadingRuns ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable data-help-section="payroll.gratuity.runsTable" pageKey="gratuity-runs" columns={runColumns} rows={runs} getRowKey={r => r.id}
                    emptyMessage="No gratuity runs yet." />
            )}

            <ConfirmDialog
                open={!!reverseTarget} title="Reverse gratuity run" confirmLabel="Reverse" busy={reversing}
                message={reverseTarget && `Reverse the gratuity provision for ${periodLabel(reverseTarget.year, reverseTarget.month)}? Its journal entry is reversed.`}
                onConfirm={reverse} onClose={() => setReverseTarget(null)}
            />

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {detail && `${humanizeEnum(detail.run.kind)} run, ${periodLabel(detail.run.year, detail.run.month)}`}
                </DialogTitle>
                <DialogContent dividers>
                    {detail && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                                <RunStatusChip status={detail.run.status} />
                                <JournalLink id={detail.run.journalEntryId} />
                                {detail.run.reversalJournalEntryId && <JournalLink id={detail.run.reversalJournalEntryId} label="View reversal JE" />}
                            </Box>
                            {detail.run.kind === 'UTILISATION' && (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    This run was created automatically when a final settlement was paid. It cannot be reversed here;
                                    it is reversed by voiding the settlement.
                                </Alert>
                            )}
                            <ResponsiveTable data-help-section="payroll.gratuity.detailTable" pageKey="gratuity-run-lines" columns={lineColumns(detail.run.kind === 'UTILISATION')}
                                rows={detail.lines} getRowKey={l => l.userId} emptyMessage="No lines." />
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button data-help="payroll.gratuity.detailClose" onClick={() => setDetail(null)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default GratuityProvisionTab;
